import { alpha, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormLabel, Grid, MenuItem, Radio, RadioGroup, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useModuleAccess } from "../../../../hooks/useModuleAccess";
import { addYears, format, parseISO, startOfToday } from "date-fns";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { Colors } from "../../../../styles/theme";
import { SelectPractioner } from "../../../common";
import AppDialogTitle from "../../../ui/appDialogTitle";
import SimpleDatePicker from "../../../ui/simpleDatePicker";
import { RxService } from "../../../../service/rxService";
import { toast } from "react-toastify";
import CustomerInfobar from "../../customerInfobar";
import useLocation from "../../../../hooks/useLocation";
import { useCan } from "../../../../hooks/useCan";
import { 
    axisValues, 
    addValues, 
    rxModel,
    parseForDatabase,
    formatForDisplay 
} from "../../../../utils/rxValues";
import { 
    SPHERE_MIN, 
    SPHERE_MAX, 
    SPHERE_INCREMENT,
    CYLINDER_MIN,
    CYLINDER_MAX,
    CYLINDER_INCREMENT
} from "../../../../utils/prescriptionConstants";
import SignedValueSelector from "./SignedValueSelector";
import { usePrint } from "../../../../hooks/usePrint";
import PrintPreviewDialog from "../../../print/PrintPreviewDialog";
import SendRxEmailDialog from "./SendRxEmailDialog";

export default function EditRxDialog({ onClose, open, customer, prescriptionData, onUpdated }) {
    const { selectedLocation } = useLocation();
    const can = useCan();
    const moduleAccess = useModuleAccess();
    const { printPDF, previewUrl, closePreview, isGenerating, cleanup } = usePrint();
    const [sendEmailDialog, setSendEmailDialog] = useState({ open: false, rx: null });

    const handlePractitionerSourceChange = (event) => {
        const practitionerSource = event.target.value;
        formik.setFieldValue('practitionerSource', practitionerSource);

        if (practitionerSource === 'internal') {
            formik.setFieldValue('externalPractitionerName', '');
            formik.setFieldValue('externalClinicName', '');
            return;
        }

        formik.setFieldValue('practitionerId', '');
    };

    const formik = useFormik({
        initialValues: { 
            ...rxModel, 
            customerId: customer?.customer?.id,
            locationId: null
        },
        onSubmit: async (data) => {
            try {
                if (data.practitionerSource === 'internal' && !data.practitionerId) {
                    toast.error('Please select a practitioner');
                    return;
                }
                if (data.practitionerSource === 'external' && !data.externalPractitionerName?.trim()) {
                    toast.error('Please enter an external practitioner name');
                    return;
                }

                const cleanData = {
                    ...data,
                    odSph: parseForDatabase.sphere(data.odSph),
                    odCyl: parseForDatabase.cylinder(data.odCyl),
                    odAxis: parseForDatabase.axis(data.odAxis),
                    odAdd: data.odAdd || null,
                    osSph: parseForDatabase.sphere(data.osSph),
                    osCyl: parseForDatabase.cylinder(data.osCyl),
                    osAxis: parseForDatabase.axis(data.osAxis),
                    osAdd: data.osAdd || null,
                    odDistPd: data.odDistPd || null,
                    odNearPd: data.odNearPd || null,
                    osDistPd: data.osDistPd || null,
                    osNearPd: data.osNearPd || null,
                    odPrism: data.odPrism || null,
                    osPrism: data.osPrism || null,
                    externalPractitionerName: data.externalPractitionerName?.trim() || null,
                    externalClinicName: data.externalClinicName?.trim() || null
                };
                await RxService.update(cleanData);
                toast.success('Rx updated successfully');
                if (onUpdated) onUpdated(cleanData);
                onClose();
            } catch (error) {
                toast.error('Failed to update rx. Please try again.');
                console.error('Error updating rx:', error);
            }
        }
    });

    const handleSignedValueChange = (fieldName, fieldValue) => {
        formik.setFieldValue(fieldName, fieldValue);
        formik.setFieldTouched(fieldName, true, false);
    };

    useEffect(() => {
        if (prescriptionData && open) {
            formik.setValues({
                ...prescriptionData,
                customerId: customer?.customer?.id,
                practitionerSource: prescriptionData.practitionerSource || 'internal',
                practitionerId: prescriptionData.practitionerId || '',
                externalPractitionerName: prescriptionData.externalPractitionerName || '',
                externalClinicName: prescriptionData.externalClinicName || '',
                rxType: prescriptionData.rxType || 'glasses',
                odSph: prescriptionData.odSph ? formatForDisplay.sphere(prescriptionData.odSph) : '',
                odCyl: prescriptionData.odCyl ? formatForDisplay.cylinder(prescriptionData.odCyl) : '',
                odAxis: prescriptionData.odAxis ? formatForDisplay.axis(prescriptionData.odAxis) : '',
                odAdd: prescriptionData.odAdd ? formatForDisplay.add(prescriptionData.odAdd) : '',
                osSph: prescriptionData.osSph ? formatForDisplay.sphere(prescriptionData.osSph) : '',
                osCyl: prescriptionData.osCyl ? formatForDisplay.cylinder(prescriptionData.osCyl) : '',
                osAxis: prescriptionData.osAxis ? formatForDisplay.axis(prescriptionData.osAxis) : '',
                osAdd: prescriptionData.osAdd ? formatForDisplay.add(prescriptionData.osAdd) : '',
                odDistPd: prescriptionData.odDistPd ?? '',
                odNearPd: prescriptionData.odNearPd ?? '',
                osDistPd: prescriptionData.osDistPd ?? '',
                osNearPd: prescriptionData.osNearPd ?? '',
                odPrism: prescriptionData.odPrism ?? '',
                osPrism: prescriptionData.osPrism ?? '',
                odPrismBase: prescriptionData.odPrismBase ?? '',
                osPrismBase: prescriptionData.osPrismBase ?? '',
                odBc: prescriptionData.odBc ?? '',
                osBc: prescriptionData.osBc ?? '',
                odContactLensDiameter: prescriptionData.odContactLensDiameter ?? '',
                osContactLensDiameter: prescriptionData.osContactLensDiameter ?? '',
                odK1: prescriptionData.odK1 ?? '',
                osK2: prescriptionData.osK2 ?? '',
                odVa: prescriptionData.odVa ?? '',
                osVa: prescriptionData.osVa ?? '',
                notes: prescriptionData.notes ?? '',
                rxDate: prescriptionData.rxDate ?? '',
                expiryDate: prescriptionData.expiryDate ?? '',
                locationId: prescriptionData?.locationId || selectedLocation?.id || null
            });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prescriptionData, open]);

    // Cleanup print resources when dialog closes
    useEffect(() => {
        if (!open) {
            cleanup();
        }
    }, [open, cleanup]);

    function setExpiryDate(years) {
        const rxDate = formik.values.rxDate || format(startOfToday(), 'yyyy-MM-dd');
        const parsedDate = parseISO(rxDate);
        const newExpiryDate = addYears(parsedDate, years);
        formik.setFieldValue('expiryDate', format(newExpiryDate, 'yyyy-MM-dd'), false);
    }

    const printRx = () => {
        printPDF(() => RxService.print(prescriptionData.id));
    };

    const handleSendEmail = () => {
        setSendEmailDialog({
            open: true,
            rx: {
                id: prescriptionData?.id
            }
        });
    };

    return (
        <>
        <Dialog onClose={onClose} open={open} fullWidth maxWidth={"lg"}>
            <AppDialogTitle onClose={onClose} title="Edit Rx" />
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Box display="flex" alignItems="baseline" sx={{ p: 1, borderBottom: '1px solid #ccc', backgroundColor: '#f9f9f9', pb: 1 }}>
                        <CustomerInfobar customerData={customer} />
                    </Box>
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                        <Grid item xs={12} md={4} sx={{ background: '#f6f6f6', p: 1 }}>
                            <Stack>
                                <FormControl sx={{ mb: 2 }}>
                                    <FormLabel>Practitioner Source</FormLabel>
                                    <RadioGroup
                                        row
                                        name="practitionerSource"
                                        onChange={handlePractitionerSourceChange}
                                        value={formik.values.practitionerSource}
                                    >
                                        <FormControlLabel value="internal" control={<Radio />} label="Internal" />
                                        <FormControlLabel value="external" control={<Radio />} label="External" />
                                    </RadioGroup>
                                </FormControl>

                                {formik.values.practitionerSource === 'internal' ? (
                                    <SelectPractioner
                                        sx={{ mb: 2 }}
                                        setPractitioner={(practitioner) => formik.setFieldValue('practitionerId', practitioner)}
                                        practitioner={formik.values.practitionerId} />
                                ) : (
                                    <Stack spacing={2} sx={{ mb: 2 }}>
                                        <TextField
                                            size="small"
                                            label="External Practitioner"
                                            value={formik.values.externalPractitionerName}
                                            onChange={(event) => formik.setFieldValue('externalPractitionerName', event.target.value)}
                                            required
                                        />
                                    </Stack>
                                )}

                                <TextField
                                    size="small"
                                    label="Practitioner"
                                    value={formik.values.practitionerSource === 'internal' ? 'Internal' : 'External'}
                                    InputProps={{ readOnly: true }}
                                />
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ background: '#f6f6f6', p: 1 }}>
                            <Stack>
                                <FormControl>
                                    <FormLabel>Rx Type</FormLabel>
                                    <RadioGroup
                                        row
                                        name="rxType"
                                        onChange={formik.handleChange}
                                        value={formik.values.rxType}
                                    >
                                        <FormControlLabel value="glasses" control={<Radio />} label="Glasses" />
                                        <FormControlLabel value="contacts" control={<Radio />} label="Contacts" />
                                    </RadioGroup>
                                </FormControl>

                                <SimpleDatePicker
                                    label={"Rx Date"}
                                    onChange={(value) => formik.setFieldValue('rxDate', value)}
                                    value={formik.values.rxDate}
                                />
                                <SimpleDatePicker
                                    label={"Expiry Date"}
                                    onChange={(value) => formik.setFieldValue('expiryDate', value)}
                                    value={formik.values.expiryDate}
                                />
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setExpiryDate(1)}
                                    >
                                        +1 Year
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => setExpiryDate(2)}
                                    >
                                        +2 Years
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>

                        <Grid item xs={12} md={4} sx={{ background: '#f6f6f6', p: 1 }}>
                            <Stack spacing={2}>
                                <TextField
                                    size="small"
                                    label="Location"
                                    value={prescriptionData?.location?.name || selectedLocation?.name || 'No location selected'}
                                    InputProps={{ readOnly: true }}
                                />

                                {formik.values.practitionerSource === 'external' ? (
                                    <TextField
                                        size="small"
                                        label="External Clinic"
                                        value={formik.values.externalClinicName}
                                        onChange={(event) => formik.setFieldValue('externalClinicName', event.target.value)}
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Select Internal or External practitioner source. External prescriptions can optionally include clinic name.
                                    </Typography>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>

                    <TableContainer>
                        <Table size="small">
                            <TableHead sx={{ background: alpha(Colors.primary, 0.5) }}>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell>Sphere</TableCell>
                                    <TableCell>Cylinder</TableCell>
                                    <TableCell>Axis</TableCell>
                                    <TableCell>Add</TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>Prism</TableCell>
                                            <TableCell>Base</TableCell>
                                            <TableCell>D PD</TableCell>
                                            <TableCell>N PD</TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>BC</TableCell>
                                            <TableCell>Diameter</TableCell>
                                            <TableCell>K1</TableCell>
                                            <TableCell>K2</TableCell>
                                        </>
                                    )}
                                    <TableCell>VA</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    <TableCell>OD</TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="odSph"
                                            value={formik.values.odSph}
                                            min={SPHERE_MIN}
                                            max={SPHERE_MAX}
                                            step={SPHERE_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="odCyl"
                                            value={formik.values.odCyl}
                                            min={CYLINDER_MIN}
                                            max={CYLINDER_MAX}
                                            step={CYLINDER_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="odAxis"
                                            onChange={formik.handleChange}
                                            value={formik.values.odAxis}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="odAdd"
                                            onChange={formik.handleChange}
                                            value={formik.values.odAdd}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="odPrism"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odPrism}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odPrismBase"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odPrismBase}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odDistPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odDistPd}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odNearPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odNearPd}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="odBc"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odBc}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odContactLensDiameter"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odContactLensDiameter}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odK1"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odK1}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="odK2"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.odK2}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        <TextField
                                            name="odVa"
                                            onChange={formik.handleChange}
                                            value={formik.values.odVa}
                                            size="small" />
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            OS
                                            <Button
                                                size="small"
                                                variant="text"
                                                onClick={() => {
                                                    formik.setFieldValue('osSph', formik.values.odSph);
                                                    formik.setFieldValue('osCyl', formik.values.odCyl);
                                                    formik.setFieldValue('osAxis', formik.values.odAxis);
                                                    formik.setFieldValue('osAdd', formik.values.odAdd);
                                                    formik.setFieldValue('osPrism', formik.values.odPrism);
                                                    formik.setFieldValue('osPrismBase', formik.values.odPrismBase);
                                                    formik.setFieldValue('osDistPd', formik.values.odDistPd);
                                                    formik.setFieldValue('osNearPd', formik.values.odNearPd);
                                                    formik.setFieldValue('osVa', formik.values.odVa);
                                                    if (formik.values.rxType !== 'glasses') {
                                                        formik.setFieldValue('osBc', formik.values.odBc);
                                                        formik.setFieldValue('osContactLensDiameter', formik.values.odContactLensDiameter);
                                                        formik.setFieldValue('osK1', formik.values.odK1);
                                                        formik.setFieldValue('osK2', formik.values.odK2);
                                                    }
                                                }}
                                                sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
                                            >
                                                Copy OD
                                            </Button>
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="osSph"
                                            value={formik.values.osSph}
                                            min={SPHERE_MIN}
                                            max={SPHERE_MAX}
                                            step={SPHERE_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <SignedValueSelector
                                            name="osCyl"
                                            value={formik.values.osCyl}
                                            min={CYLINDER_MIN}
                                            max={CYLINDER_MAX}
                                            step={CYLINDER_INCREMENT}
                                            onChange={handleSignedValueChange}
                                            textFieldSx={{ minWidth: 80 }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="osAxis"
                                            onChange={formik.handleChange}
                                            value={formik.values.osAxis}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {axisValues.map(val => <MenuItem key={val} value={val}>{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            name="osAdd"
                                            onChange={formik.handleChange}
                                            value={formik.values.osAdd}
                                            size="small"
                                            displayEmpty
                                            sx={{ minWidth: 80 }}>
                                            <MenuItem value="">-</MenuItem>
                                            {addValues.map(val => <MenuItem key={val} value={val}>+{val}</MenuItem>)}
                                        </Select>
                                    </TableCell>
                                    {formik.values.rxType === 'glasses' && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="osPrism"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osPrism}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osPrismBase"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osPrismBase}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osDistPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osDistPd}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osNearPd"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osNearPd}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    {(formik.values.rxType === 'contacts') && (
                                        <>
                                            <TableCell>
                                                <TextField
                                                    name="osBc"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osBc}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osContactLensDiameter"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osContactLensDiameter}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osK1"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osK1}
                                                    size="small" />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    name="osK2"
                                                    onChange={formik.handleChange}
                                                    value={formik.values.osK2}
                                                    size="small" />
                                            </TableCell>
                                        </>
                                    )}
                                    <TableCell>
                                        <TextField
                                            name="osVa"
                                            onChange={formik.handleChange}
                                            value={formik.values.osVa}
                                            size="small" />
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TextField
                        sx={{ mb: 1, mt: 2 }}
                        name="notes"
                        onChange={formik.handleChange}
                        value={formik.values.notes}
                        multiline
                        rows={4}
                        label="Notes"
                        variant="standard"
                        fullWidth />

                </DialogContent>
                <DialogActions>
                    <Button color="primary" variant="contained" type="button" onClick={printRx}>
                        {isGenerating ? <CircularProgress size={24} /> : 'Print'}
                    </Button>
                    {can.action('messaging', 'send') && moduleAccess?.isEnabled('email') && (
                        <Button
                            color="info"
                            variant="outlined"
                            type="button"
                            onClick={handleSendEmail}
                            disabled={!prescriptionData?.id || !customer?.customer?.email}
                        >
                            Send Email
                        </Button>
                    )}
                    <Button color="primary" variant="contained" type="submit" autoFocus>
                        Update
                    </Button>
                    <Button color="cancel" variant="outlined" onClick={onClose}>Cancel</Button>
                </DialogActions>
            </form>
        </Dialog>
        <SendRxEmailDialog
            open={sendEmailDialog.open}
            recipient={sendEmailDialog.rx ? {
                recipientId: customer?.customer?.id,
                email: customer?.customer?.email,
                name: customer?.customer?.name || `${customer?.customer?.firstName || ''} ${customer?.customer?.lastName || ''}`.trim()
            } : null}
            entityId={sendEmailDialog.rx?.id}
            onClose={() => setSendEmailDialog({ open: false, rx: null })}
            onSuccess={(message) => {
                toast.success(message);
                setSendEmailDialog({ open: false, rx: null });
            }}
        />
        <PrintPreviewDialog url={previewUrl} onClose={closePreview} />
        </>
    );
}